
import { Helmet } from "react-helmet";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>Terms of Service - Feednatic</title>
        <meta
          name="description"
          content="Terms of Service for Feednatic - Learn about the terms governing your use of our platform."
        />
      </Helmet>

      {/* Header/Navigation */}
      <header className="border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="lovable-uploads/48a2ed1e-15ea-4dbf-bdbc-f70f710ca82a.png" alt="Logo" className="h-8" />
            <span className="font-bold text-xl hidden sm:inline-block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Feednatic
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/blog")}>
              Blog
            </Button>
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/auth")}>
              Log in
            </Button>
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white" onClick={() => navigate("/auth")}>Sign up</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button variant="ghost" className="mb-6 flex items-center gap-2 text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        <h1 className="text-5xl font-black mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Terms of Service</h1>
        <p className="text-white/60 mb-4">Last updated: May 20, 2025</p>
        
        <div className="space-y-8">
          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">1. Agreement to Terms</h2>
            <p className="text-white/70">
              By accessing or using Feednatic's platform ("Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Platform.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">2. Description of Service</h2>
            <p className="text-white/70">
              Feednatic provides a feedback management platform that allows companies to collect, organize, and prioritize user feedback. Our Platform includes features such as feedback boards, roadmaps, AI live chat support, and knowledge base functionality.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">3. User Accounts</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">3.1 Account Creation</h3>
                <p className="text-white/70">
                  To use certain features of our Platform, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">3.2 Account Security</h3>
                <p className="text-white/70">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">4. Subscription and Payment</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">4.1 Subscription Plans</h3>
                <p className="text-white/70">
                  We offer various subscription plans with different features and pricing. You can choose the plan that best suits your needs. Some features may only be available in specific plans.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">4.2 Payment</h3>
                <p className="text-white/70">
                  You agree to pay the fees for the subscription plan you select. Payments are processed securely through our payment processors. By providing your payment information, you authorize us to charge your payment method for the fees associated with your subscription.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">4.3 Cancellation and Refunds</h3>
                <p className="text-white/70">
                  You can cancel your subscription at any time. Refunds are provided in accordance with our refund policy, which may vary depending on the circumstances of the cancellation.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">5. User Content</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">5.1 Content Ownership</h3>
                <p className="text-white/70">
                  You retain ownership of any content you submit to our Platform. However, by submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, and distribute your content in connection with our services.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">5.2 Content Guidelines</h3>
                <p className="text-white/70">
                  You agree not to submit content that is unlawful, defamatory, harassing, abusive, fraudulent, or otherwise objectionable. We reserve the right to remove any content that violates these guidelines or our policies.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">6. Intellectual Property</h2>
            <p className="text-white/70">
              The Platform and its original content, features, and functionality are owned by Feednatic and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">7. Limitation of Liability</h2>
            <p className="text-white/70">
              In no event shall Feednatic, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Platform.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">8. Termination</h2>
            <p className="text-white/70">
              We may terminate or suspend your account and access to our Platform immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms of Service.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">9. Changes to Terms</h2>
            <p className="text-white/70">
              We reserve the right to modify or replace these Terms of Service at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">10. Contact Us</h2>
            <p className="text-white/70">
              If you have any questions about these Terms of Service, please contact us at support@feednatic.com.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;
